import React from 'react';
import { Text as RNText, TextStyle } from 'react-native';
import { Theme } from './theme';

/**
 * Simple markdown formatter for chat messages
 * Supports: **bold**, *italic*, `code`, and line breaks
 */
export const formatMarkdown = (text: string, baseStyle?: TextStyle): React.ReactNode => {
  const elements: React.ReactNode[] = [];
  let keyCounter = 0;

  // Patterns for markdown (order matters!)
  const patterns = [
    { regex: /\*\*(.+?)\*\*/g, style: { fontFamily: Theme.typography.fontFamily.semiBold } }, // Bold
    { regex: /\*(.+?)\*/g, style: { fontStyle: 'italic' as const } }, // Italic
    { regex: /`(.+?)`/g, style: { fontFamily: 'Courier', backgroundColor: 'rgba(0,0,0,0.05)', paddingHorizontal: 4, borderRadius: 4 } }, // Code
  ];

  // Process text line by line to preserve line breaks
  const lines = text.split('\n');
  
  lines.forEach((line, lineIndex) => {
    let segments: Array<{ text: string; style?: TextStyle }> = [{ text: line }];

    // Apply each pattern sequentially
    patterns.forEach(({ regex, style }) => {
      const newSegments: Array<{ text: string; style?: TextStyle }> = [];
      
      segments.forEach(segment => {
        if (segment.style) {
          // Already styled, don't process further
          newSegments.push(segment);
          return;
        }

        let lastIndex = 0;
        let match;
        const tempRegex = new RegExp(regex.source, regex.flags);
        
        while ((match = tempRegex.exec(segment.text)) !== null) {
          // Add text before match
          if (match.index > lastIndex) {
            newSegments.push({ text: segment.text.substring(lastIndex, match.index) });
          }
          
          // Add matched text with style
          newSegments.push({ text: match[1], style });
          
          lastIndex = tempRegex.lastIndex;
        }
        
        // Add remaining text
        if (lastIndex < segment.text.length) {
          newSegments.push({ text: segment.text.substring(lastIndex) });
        }
      });
      
      segments = newSegments;
    });

    // Convert segments to React elements
    segments.forEach(segment => {
      if (segment.text) {
        elements.push(
          <RNText key={`segment-${keyCounter++}`} style={[baseStyle, segment.style]}>
            {segment.text}
          </RNText>
        );
      }
    });

    // Add line break if not the last line
    if (lineIndex < lines.length - 1) {
      elements.push(<RNText key={`newline-${keyCounter++}`}>{'\n'}</RNText>);
    }
  });

  // Return a single React fragment instead of array to avoid text node issues
  return <>{elements}</>;
};

